import { useState } from 'react';
import { rsme, type FleetQueryFilters } from '../lib/rsme';
import { useQuery } from '../ui/useQuery';
import { PageHead, Card, Pill, Input, Button, Loading, ErrorState, EmptyState } from '../ui/primitives';

const today = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

export default function Availability() {
  const [tonnage, setTonnage] = useState('');
  const [date, setDate] = useState(today());
  const [endDate, setEndDate] = useState('');
  const [brand, setBrand] = useState('');

  const filters: FleetQueryFilters = {
    tonnage: tonnage ? Number(tonnage) : undefined,
    date: date || undefined,
    endDate: endDate || undefined,
    brand: brand || undefined,
    status: 'all',
  };
  const q = useQuery(() => rsme.fleet.query(filters), [tonnage, date, endDate, brand]);

  return (
    <div className="max-w-6xl mx-auto">
      <PageHead title="Availability" sub="The /fleet/query endpoint — what's free, what's booked, what's in repair" />

      <Card className="mx-5 md:mx-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <Field label="Min tonnage (T)"><Input value={tonnage} onChange={(e) => setTonnage(e.target.value)} placeholder="e.g. 5" inputMode="numeric" /></Field>
          <Field label="From"><Input value={date} onChange={(e) => setDate(e.target.value)} placeholder="DD/MM/YYYY" /></Field>
          <Field label="To"><Input value={endDate} onChange={(e) => setEndDate(e.target.value)} placeholder="DD/MM/YYYY (optional)" /></Field>
          <Field label="Brand"><Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. TCM" /></Field>
          <Button onClick={q.refetch} className="self-end">Refresh</Button>
        </div>
      </Card>

      {q.loading && <div className="mt-4"><Loading rows={4} /></div>}
      {q.error && <ErrorState error={q.error} retry={q.refetch} />}

      {q.data && (
        <div className="px-5 md:px-8 mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat tone="good" label="Available" value={q.data.counts.available} />
            <Stat tone="accent" label="On rent" value={q.data.counts.unavailable} />
            <Stat tone="warn" label="In repair" value={q.data.counts.underRepair} />
            <Stat tone="muted" label="Reserved" value={q.data.counts.reserved} />
          </div>

          <Section title="Available" tone="good" assets={q.data.available} />
          <Section title="On rent" tone="accent" assets={q.data.unavailable} bookingKey="booking" />
          <Section title="In repair" tone="warn" assets={q.data.underRepair} bookingKey="nextBooking" />

          {!q.data.assets.length && <EmptyState title="No assets match" />}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-muted mb-1">{label}</div>
      {children}
    </label>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'good' | 'warn' | 'danger' | 'accent' | 'muted' }) {
  return (
    <Card className="!p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      <div className="mt-2"><Pill tone={tone}>{tone === 'good' ? 'ready' : tone === 'accent' ? 'in use' : tone === 'warn' ? 'attention' : 'parked'}</Pill></div>
    </Card>
  );
}

function Section({ title, tone, assets, bookingKey }: { title: string; tone: 'good' | 'warn' | 'accent'; assets: any[]; bookingKey?: 'booking' | 'nextBooking' }) {
  if (!assets?.length) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2"><Pill tone={tone}>{title}</Pill><span className="text-xs text-muted">{assets.length}</span></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {assets.map((a) => (
          <Card key={a.id} className="!p-4">
            <div className="font-medium truncate">{a.assetName}</div>
            <div className="text-xs text-muted truncate">{a.brand} {a.model} · {a.tonnage}T · {a.mastType ?? '—'}</div>
            {bookingKey && a[bookingKey] && (
              <div className="text-xs mt-2 text-muted">
                {bookingKey === 'booking' ? 'booked' : 'next out'} {a[bookingKey].from} → {a[bookingKey].to}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
